import { useCallback, useMemo, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BOSSES, KINGDOMS, REGION_MAPS, TOWNS, ghosts } from '@yuan-math/content';
import { layoutConceptGraph } from '@yuan-math/curriculum';
import { namedTricksOf, type MasteryLevel } from '@yuan-math/learning';
import {
  bossCleared,
  bossReadiness,
  ghostIndex,
  hauntings,
  regionMapOf,
  townOf,
  worldState,
  type BossDef,
  type Haunting,
  type Kingdom,
  type KingdomState,
  type Region,
  type RegionProgress,
} from '@yuan-math/world';
import { Card, ProgressRing, SectionHeading } from '@yuan-math/ui';
import type { LearnerProfile } from '@yuan-math/learning';
import { registry, useLearner } from '../../state/LearnerContext.tsx';
import { KingdomStatusChip, sealClause } from '../../components/WorldMap.tsx';
import { BANDS, MasteryBand, PIP_COUNT } from '../../components/MasteryBand.tsx';
import { QuestBoard } from '../../components/QuestBoard.tsx';
import { TownMap } from '../../components/TownMap.tsx';
import { graphPositions } from '../../components/conceptLayout.ts';
import { NotFoundPage } from '../NotFoundPage.tsx';

/**
 * `/adventure/kingdoms/:kingdomId` — one kingdom, in full (docs/rpg.md §5, §8,
 * §22): its regions, the skill tree underneath them, and the doors the quest
 * line and the boss will arrive through.
 *
 * **A seal withholds the island, never the mathematics**
 * (`packages/world/src/types.ts`). A sealed kingdom does not draw any of this:
 * it draws `SealedKingdom` at the bottom of this file, which states the seal as
 * mathematics and hands over every concept it is waiting for as a link. What
 * the seal holds back is the island — the village, the regions, the quest line,
 * the boss ceremony, the rewards, and the kingdoms past it. It has never held
 * back a lesson and this page must never be the first place it does: the
 * concept pages, the courses and the Math Map are all reachable from the sealed
 * screen, and `canOpenLesson()` still returns true unconditionally. Nothing
 * here reads XP, level or gold either; status and readiness come from
 * `worldState`, which reads `knownSet`.
 *
 * The skill tree is the Math Map's machinery pointed at one kingdom:
 * `layoutConceptGraph` does the graph reasoning, `graphPositions` turns layers
 * into coordinates, and this file only decides what a node looks like.
 */

/* -------------------------------------------------------------------------- */
/* The skill tree                                                             */
/* -------------------------------------------------------------------------- */

/*
 * Smaller boxes than the Math Map's, because a kingdom is drawn at its natural
 * size inside a scrolling frame rather than fitted to a window: the widest
 * kingdom is six columns, and six columns of Math Map nodes would need a
 * horizontal scroll on a laptop.
 */
/** The bestiary is a pure function of static content, so it is built once. */
const BESTIARY = ghostIndex(ghosts);

const NODE_WIDTH = 146;
const NODE_HEIGHT = 52;
const COLUMN_GAP = 30;
const ROW_GAP = 56;

function SkillTree({ conceptIds, masteryOf }: { conceptIds: string[]; masteryOf: (id: string) => { level: MasteryLevel } }): JSX.Element {
  const include = useMemo(() => new Set(conceptIds), [conceptIds]);
  const layout = useMemo(() => layoutConceptGraph(registry.graph, include), [include]);
  const positions = useMemo(
    () => graphPositions(layout, { nodeWidth: NODE_WIDTH, nodeHeight: NODE_HEIGHT, columnGap: COLUMN_GAP, rowGap: ROW_GAP }),
    [layout],
  );

  return (
    <div className="kingdom-tree__canvas" role="region" aria-label="Skill tree, scrollable" tabIndex={0}>
      <svg
        className="kingdom-tree__svg"
        viewBox={`0 0 ${positions.width} ${positions.height}`}
        width={positions.width}
        height={positions.height}
        /*
         * One picture with one description. The concepts it draws are all
         * linked, named and banded in the region cards above, so making forty
         * SVG boxes forty extra tab stops would add no information and a great
         * deal of tabbing — the drawing is the alternative view, not the only
         * one.
         */
        role="img"
        aria-label="The kingdom's concepts, drawn with a line from each concept up to the ones built on it. Every concept is listed with its mastery in the regions above."
      >
        <g className="kingdom-tree__edges">
          {conceptIds.flatMap((id) =>
            (registry.graph.prerequisites.get(id) ?? []).map((prereqId) => {
              if (!include.has(prereqId)) return null;
              const from = positions.map.get(prereqId);
              const to = positions.map.get(id);
              if (!from || !to) return null;
              const x1 = from.x + NODE_WIDTH / 2;
              const y1 = from.y + NODE_HEIGHT;
              const x2 = to.x + NODE_WIDTH / 2;
              const y2 = to.y;
              const midY = (y1 + y2) / 2;
              return (
                <path
                  key={`${prereqId}->${id}`}
                  className="kingdom-tree__edge"
                  d={`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`}
                  fill="none"
                />
              );
            }),
          )}
        </g>

        <g className="kingdom-tree__nodes">
          {conceptIds.map((id) => {
            const position = positions.map.get(id);
            const concept = registry.concept(id);
            if (!position || !concept) return null;
            const level = masteryOf(id).level;
            const { label, pips } = BANDS[level];

            return (
              <g
                key={id}
                className={`kingdom-tree__node tree-band--${level}`}
                data-concept-id={id}
                transform={`translate(${position.x}, ${position.y})`}
              >
                <title>{`${concept.title} — ${label}`}</title>
                <rect className="kingdom-tree__box" width={NODE_WIDTH} height={NODE_HEIGHT} rx={10} />
                <g className="kingdom-tree__pips">
                  {Array.from({ length: PIP_COUNT }, (_, index) => (
                    <circle
                      key={index}
                      className={`kingdom-tree__pip ${index < pips ? 'kingdom-tree__pip--on' : ''}`}
                      cx={12 + index * 11}
                      cy={NODE_HEIGHT - 10}
                      r={3.5}
                    />
                  ))}
                </g>
                <foreignObject x={9} y={5} width={NODE_WIDTH - 18} height={NODE_HEIGHT - 22}>
                  <span className="kingdom-tree__title">{concept.title}</span>
                </foreignObject>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Regions                                                                    */
/* -------------------------------------------------------------------------- */

function RegionCard({
  region,
  progress,
  masteryOf,
  haunted,
  onWalk,
}: {
  region: Region;
  progress: RegionProgress;
  masteryOf: (id: string) => { level: MasteryLevel };
  /** The region's unsealed hauntings, in map order. Empty for a quiet region. */
  haunted: { conceptId: string; title: string }[];
  /** Given when the region has a map of its own — the second way in. */
  onWalk: (() => void) | undefined;
}): JSX.Element {
  return (
    /*
     * Named after the region itself, because the town map's signposts point
     * here: walking to Sum Cottage scrolls to Sum Cottage's card, which is
     * where the region is stated in full. One id, in the one place a region is
     * written out.
     */
    <Card as="li" className="region-card" id={region.id}>
      <h3 className="region-card__name">
        <span aria-hidden="true">{region.emoji}</span> {region.name}
      </h3>
      <p className="region-card__flavor">{region.flavor}</p>
      <p className="region-card__progress">
        <strong>
          {progress.known}/{progress.total}
        </strong>{' '}
        skills known · {progress.mastered} mastered by practice
      </p>

      {/*
        What is loose in here, from the student's own recent mistakes.
        Deliberately worded as a place to go rather than as a score: it is a
        count of *sites worth visiting*, and it disappears the moment the
        practice stops going wrong. Absent, not zero — "0 haunted" on every
        quiet region would turn the whole page into a scoreboard for something
        nobody is trying to win.
      */}
      {haunted.length > 0 ? (
        <p className="region-card__haunted">
          <span aria-hidden="true">👻</span> {haunted.length} haunted{' '}
          {haunted.length === 1 ? 'site' : 'sites'} — {haunted.map((entry) => entry.title).join(', ')}
        </p>
      ) : null}

      <ul className="kingdom-concepts">
        {region.conceptIds.map((conceptId) => {
          const concept = registry.concept(conceptId);
          if (!concept) return null;
          /*
           * A planned concept has a concept page — it simply has no lessons
           * behind it yet — so it is a real link and says why it is quiet,
           * rather than a greyed-out row that looks like something the student
           * has failed to reach.
           */
          const planned = concept.status === 'planned';
          return (
            <li key={conceptId} className={`kingdom-concept ${planned ? 'kingdom-concept--planned' : ''}`}>
              <Link to={`/concepts/${conceptId}`} className="kingdom-concept__link">
                {concept.title}
              </Link>
              {planned ? (
                <span className="kingdom-concept__horizon">On the horizon</span>
              ) : (
                <MasteryBand level={masteryOf(conceptId).level} />
              )}
            </li>
          );
        })}
      </ul>

      {/*
        The map is the picture and this card is the index, so the card is never
        behind the map: it links *to* it. An anchor as well as a handler,
        because the map is further up the page and a student who clicked "walk
        it" and stayed put would think nothing had happened.
      */}
      {onWalk ? (
        <a href="#kingdom-map" className="region-card__walk" onClick={onWalk}>
          <span aria-hidden="true">🚶</span> Walk {region.name}
        </a>
      ) : null}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* The boss gate                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The door to the fight, and the truth about walking through it.
 *
 * **The gate never refuses anybody**
 * (docs/rpg-implementation-plan.md §0.1, and `canOpenLesson()` for the same
 * reason). There is no readiness threshold here and no disabled button: the
 * gate says what the boss will ask and how much of it the student can already
 * do, and then opens. A student who wants to go and meet the dragon knowing
 * three skills of nine is allowed to, they will be told encouragingly what
 * caught them out, and finding the edge of what you know costs nothing
 * (docs/rpg.md §12).
 *
 * The count is read off `knownSet` — what the student can do, placement
 * credit included — rather than off mastery, which is the same set the rest of
 * this page's readiness is drawn from.
 */
function BossGate({
  def,
  profile,
  known,
}: {
  def: BossDef;
  profile: LearnerProfile | null;
  known: ReadonlySet<string>;
}): JSX.Element {
  const readiness = bossReadiness(def, known);
  const cleared = profile ? bossCleared(def, profile) : false;
  /*
   * Named in a sentence rather than listed as links, deliberately. Every one of
   * these concepts is already a link a few inches up the page, in the region it
   * belongs to and beside its mastery band; a second index of the same kingdom
   * would be the same information said twice, and the region cards say it
   * better.
   */
  const unlearned = readiness.unknownConceptIds.map((id) => registry.concept(id)?.title ?? id);
  const shown = unlearned.slice(0, 4);
  const more = unlearned.length - shown.length;

  return (
    <Card className={`boss-gate ${cleared ? 'boss-gate--cleared' : ''}`}>
      <div className="boss-gate__head">
        <span className="boss-gate__emoji" aria-hidden="true">
          {def.emoji}
        </span>
        <div>
          <h3 className="boss-gate__name">{def.name}</h3>
          <p className="boss-gate__meta">
            {def.stages.length} {def.stages.length === 1 ? 'stage' : 'stages'} ·{' '}
            {def.stages.reduce((sum, stage) => sum + stage.questionCount, 0)} questions · no timer
          </p>
        </div>
        {cleared ? (
          <span className="boss-gate__cleared">
            <span aria-hidden="true">👑</span> Beaten
          </span>
        ) : null}
      </div>

      <p className="boss-gate__readiness">
        You know{' '}
        <strong>
          {readiness.known} of {readiness.total}
        </strong>{' '}
        skills {def.name} tests.
      </p>

      <ul className="boss-gate__stages">
        {def.stages.map((stage, index) => (
          <li key={stage.name} className="boss-gate__stage">
            <span className="boss-gate__stage-name">
              {index + 1}. {stage.name}
            </span>
            <span className="boss-gate__stage-meta">
              {stage.questionCount} questions · difficulty {stage.difficulty} of 5
            </span>
          </li>
        ))}
      </ul>

      {unlearned.length > 0 ? (
        <p className="boss-gate__gaps">
          Still to learn: {shown.join(', ')}
          {more > 0 ? ` and ${more} more` : ''}. Nothing is stopping you going in now — the regions above are where
          those live.
        </p>
      ) : null}

      <Link to={`/adventure/boss/${def.id}`} className="btn btn--primary btn--lg boss-gate__enter">
        {cleared ? `Fight ${def.name} again` : `Challenge ${def.name}`}
      </Link>

      <p className="boss-gate__note">
        Every question comes from this kingdom&rsquo;s own practice bank, and every answer counts towards your mastery
        whichever way it goes. Losing costs nothing at all — a retry asks different questions.
      </p>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* The page                                                                   */
/* -------------------------------------------------------------------------- */

export function KingdomPage(): JSX.Element {
  const { kingdomId } = useParams();
  const { profile, masteryOf, knownConceptIds } = useLearner();

  /*
   * Which region's map the student has walked into, if any.
   *
   * Page state, deliberately, exactly like where they are standing on it: being
   * inside the cottage is not a fact about anybody's mathematics, so it is not
   * on the profile and it is not in the URL. A reload puts them back on the
   * village green, which is the useful answer as well as the cheap one.
   */
  const [insideRegionId, setInsideRegionId] = useState<string | undefined>(undefined);
  /*
   * And which one they came out of, so the village puts them back at its gate
   * rather than at the crossroads. Walking to the far end of a lane, going into
   * the barn and being returned to the middle of the village undoes the walk.
   */
  const [cameFromRegionId, setCameFromRegionId] = useState<string | undefined>(undefined);
  const enterRegion = useCallback((regionId: string) => setInsideRegionId(regionId), []);
  const leaveRegion = useCallback(() => {
    setCameFromRegionId(insideRegionId);
    setInsideRegionId(undefined);
  }, [insideRegionId]);
  const hasRegionMap = useCallback((regionId: string) => regionMapOf(REGION_MAPS, regionId) !== undefined, []);

  const kingdom = KINGDOMS.find((candidate) => candidate.id === kingdomId);
  if (!kingdom) return <NotFoundPage />;

  // Derived on every render, like mastery everywhere else: retention decays, so
  // a value memoised on the profile would go quietly out of date on the screen.
  const state: KingdomState | undefined = worldState(
    KINGDOMS,
    registry.graph,
    profile?.concepts ?? {},
    profile?.rpg?.clearedBossIds ?? [],
    Date.now(),
  ).find((candidate) => candidate.kingdomId === kingdom.id);

  const progressOf = new Map((state?.regions ?? []).map((entry) => [entry.regionId, entry.progress]));
  const readiness = state?.readiness ?? 1;
  const sealed = state?.status === 'sealed';

  /*
   * A sealed island is not this student's yet, and neither is any island past
   * it (`openDownwards` leaves `sealed` upward closed, so there is nothing
   * beyond this one to reason about separately). So the page stops here.
   *
   * What it stops *at* is the point. The seal withholds the island — the
   * village, the regions, the quest line, the boss — and it withholds no
   * mathematics: every concept the seal is waiting for is handed over as a
   * link, and the Math Map and the courses are one tap away. `canOpenLesson()`
   * still returns true unconditionally and this page is still not allowed to be
   * the first place that changes.
   */
  if (sealed && state) {
    return <SealedKingdom kingdom={kingdom} state={state} readiness={readiness} />;
  }

  /*
   * The tree draws what can be studied. A planned concept has no lesson to
   * train it with, so it belongs on the region list — labelled "on the horizon"
   * — rather than as a node in a tree of skills, where an empty box would read
   * as a skill the student has not started.
   */
  const boss = BOSSES.find((candidate) => candidate.id === kingdom.bossId);

  /*
   * The village, for the kingdoms that have been surveyed. A kingdom without a
   * town map is drawn in cards alone, exactly as all ten were before the first
   * one was drawn — the map is a picture of this page, never a part of it that
   * something else depends on.
   */
  const town = townOf(TOWNS, kingdom.id);
  const inside = regionMapOf(REGION_MAPS, insideRegionId);
  const insideRegion = inside ? kingdom.regions.find((region) => region.id === inside.regionId) : undefined;

  const treeConceptIds = kingdom.regions
    .flatMap((region) => region.conceptIds)
    .filter((id) => registry.concept(id)?.status === 'available');

  /*
   * What is loose in this kingdom, from the student's own recent mistakes.
   *
   * Derived per render like everything else here, and derived *twice* — the
   * town map computes its own from the same pure function, because both are
   * facts about the current profile and threading one down would make this
   * page responsible for keeping the map in step with itself.
   *
   * **A haunting is never a lock.** It adds a line to a card and a ghost to a
   * plate. Every region, every concept and every site opens exactly as it did
   * before one appeared.
   */
  const haunted = profile ? hauntings(BESTIARY, profile.attempts, namedTricksOf(profile)) : new Map<string, Haunting>();
  const hauntedIn = (region: Region): { conceptId: string; title: string }[] =>
    region.conceptIds
      .filter((id) => {
        const haunting = haunted.get(id);
        return haunting !== undefined && !haunting.sealed;
      })
      .map((id) => ({ conceptId: id, title: registry.concept(id)?.title ?? id }));


  const known = (state?.regions ?? []).reduce((sum, region) => sum + region.progress.known, 0);
  const total = (state?.regions ?? []).reduce((sum, region) => sum + region.progress.total, 0);

  return (
    <div className="page page--wide kingdom-page">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/adventure/map">World map</Link>
        <span aria-hidden="true">/</span>
        <span>{kingdom.name}</span>
      </nav>

      <header className="kingdom-header">
        <div className="kingdom-header__titles">
          <h1 className="page__title">
            <span aria-hidden="true">{kingdom.emoji}</span> {kingdom.name}
          </h1>
          <p className="page__lead">{kingdom.tagline}</p>
          <div className="kingdom-header__status">
            {state ? <KingdomStatusChip status={state.status} /> : null}
            <span className="kingdom-header__meta">
              {kingdom.regions.length} regions · {known}/{total} skills known
            </span>
          </div>
        </div>

        <ProgressRing
          value={readiness}
          label={`${Math.round(readiness * 100)}%`}
          caption="of the unlock requirement"
          size={96}
        />
      </header>

      {town ? (
        <section className="dashboard-section kingdom-town" id="kingdom-map">
          <SectionHeading
            title={insideRegion ? `${insideRegion.emoji} ${insideRegion.name}` : `${kingdom.name}, on foot`}
            description={inside ? inside.caption : town.caption}
          />
          <TownMap
            town={town}
            inside={inside}
            cameFrom={cameFromRegionId}
            kingdom={kingdom}
            progressOf={progressOf}
            boss={boss}
            bossBeaten={boss !== undefined && profile !== null && bossCleared(boss, profile)}
            hasMap={hasRegionMap}
            onEnter={enterRegion}
            onLeave={leaveRegion}
          />
        </section>
      ) : null}

      <section className="dashboard-section kingdom-regions">
        <SectionHeading
          title="Regions"
          description="Each region is a handful of concepts that belong together. Open any of them — sealed or not, in any order."
        />
        <ul className="region-grid">
          {kingdom.regions.map((region) => (
            <RegionCard
              key={region.id}
              region={region}
              progress={progressOf.get(region.id) ?? { known: 0, total: region.conceptIds.length, mastered: 0 }}
              masteryOf={masteryOf}
              haunted={hauntedIn(region)}
              onWalk={town && hasRegionMap(region.id) ? () => enterRegion(region.id) : undefined}
            />
          ))}
        </ul>
      </section>

      <section className="dashboard-section kingdom-tree">
        <SectionHeading
          title="Skill tree"
          description="What rests on what, inside this kingdom. A line runs from a concept up to everything built on top of it."
        />

        <div className="kingdom-tree__legend" role="list">
          {(Object.keys(BANDS) as MasteryLevel[]).map((level) => (
            <span key={level} role="listitem" className="kingdom-tree__legend-item">
              <MasteryBand level={level} />
            </span>
          ))}
        </div>

        <SkillTree conceptIds={treeConceptIds} masteryOf={masteryOf} />

        <p className="kingdom-tree__note">
          Concepts this kingdom rests on from earlier kingdoms are not drawn here — follow a concept&rsquo;s own page,
          or the Math Map, to see the whole chain.
        </p>
      </section>

      <section className="dashboard-section kingdom-quests" id="quest-board">
        <SectionHeading
          title="Quest Board"
          description="The kingdom's story, in order, and the errands around it. Every quest is a reason to open a lesson you could already open."
        />
        <QuestBoard kingdomId={kingdom.id} kingdomName={kingdom.name} profile={profile} now={Date.now()} />
      </section>

      <section className="dashboard-section kingdom-boss">
        <SectionHeading
          title="The boss gate"
          description="What the kingdom asks of you at the far end of it — staged, and drawn from the mathematics above."
        />
        {boss ? (
          <BossGate def={boss} profile={profile} known={knownConceptIds} />
        ) : (
          <p className="kingdom-boss__note">
            {kingdom.bossId
              ? `${kingdom.name}'s boss has not been written yet. Nothing here is waiting on it.`
              : `${kingdom.name} has no boss: it is finished simply by learning everything in it.`}
          </p>
        )}
      </section>

      <div className="button-row">
        <Link to="/adventure/map" className="btn btn--primary">
          Back to the world map
        </Link>
        <Link to="/map" className="btn btn--secondary">
          See this in the Math Map
        </Link>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* A sealed island                                                            */
/* -------------------------------------------------------------------------- */

/**
 * What a student sees when they arrive at a kingdom that is not theirs yet.
 *
 * It says three things and refuses one. It says which island this is, what the
 * seal is waiting for in mathematics rather than in fantasy ("Needs 80% of
 * Starter Village skills — you know 36%"), and where to go and get exactly
 * that: each missing concept by name, linked to its own page, which holds the
 * lesson and the practice behind it. It refuses the island — the village map,
 * the regions, the skill tree, the quest board and the boss gate.
 *
 * The distinction is the whole invariant. A seal withholds a story; it has
 * never withheld a lesson, and the links below are what proves it on the one
 * screen where a student is most likely to believe otherwise.
 */
function SealedKingdom({
  kingdom,
  state,
  readiness,
}: {
  kingdom: Kingdom;
  state: KingdomState;
  readiness: number;
}): JSX.Element {
  const wanted = state.missing
    .flatMap((missing) => (missing.kind === 'known' ? missing.conceptIds : []))
    .map((id) => ({ id, title: registry.concept(id)?.title ?? id }));

  return (
    <div className="page kingdom-page kingdom-page--sealed">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/adventure/map">World map</Link>
        <span aria-hidden="true">/</span>
        <span>{kingdom.name}</span>
      </nav>

      <header className="kingdom-header">
        <div className="kingdom-header__titles">
          <h1 className="page__title">
            <span aria-hidden="true">{kingdom.emoji}</span> {kingdom.name}
          </h1>
          <p className="page__lead">{kingdom.tagline}</p>
          <div className="kingdom-header__status">
            <KingdomStatusChip status={state.status} />
          </div>
        </div>

        <ProgressRing
          value={readiness}
          label={`${Math.round(readiness * 100)}%`}
          caption="towards breaking the seal"
          size={96}
        />
      </header>

      <Card className="kingdom-seal">
        <h2 className="kingdom-seal__title">
          <span aria-hidden="true">🔒</span> {kingdom.name} is still sealed
        </h2>
        <ul className="kingdom-seal__list">
          {state.missing.map((missing, index) => {
            const clause = sealClause(missing);
            return (
              <li key={index}>
                {clause.headline}
                {clause.detail ? <span className="kingdom-seal__detail"> {clause.detail}</span> : null}
              </li>
            );
          })}
        </ul>
        <p className="kingdom-seal__note">
          The seal holds back this kingdom&rsquo;s island — its village, its quest line and its boss — and the kingdoms
          beyond it. It holds back no mathematics: every skill it is waiting for is open to you right now, and working
          through them is exactly what breaks the seal.
        </p>
      </Card>

      {wanted.length > 0 ? (
        <section className="dashboard-section kingdom-seal-wanted">
          <SectionHeading
            title="What opens it"
            description="Each of these is a concept page: the lesson, the practice and everything it rests on. None of them is sealed."
          />
          <ul className="kingdom-seal-wanted__list">
            {wanted.map((concept) => (
              <li key={concept.id}>
                <Link to={`/concepts/${concept.id}`}>{concept.title}</Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="button-row">
        <Link to="/adventure/map" className="btn btn--primary">
          Back to the world map
        </Link>
        <Link to="/map" className="btn btn--secondary">
          See this in the Math Map
        </Link>
        <Link to="/courses" className="btn btn--secondary">
          Browse the courses
        </Link>
      </div>
    </div>
  );
}
